/**
 * Admin Layout Component
 *
 * Provides sidebar navigation for the admin section.
 * Nav items are filtered by deployment mode and user role.
 */

import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useRoleAssumption, ROLE_LABELS } from '../../context/RoleAssumptionContext';
import RoleSwitcher from '../../components/layout/RoleSwitcher';
import { useFeatureConfig, useFeature, type FeatureConfig } from '../../config/features';
import './AdminLayout.css';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  roles?: string[];
  /** Feature flag required for this nav item to appear */
  feature?: keyof FeatureConfig;
}

const navItems: NavItem[] = [
  { path: '/admin', label: 'Dashboard', icon: '📊' },
  { path: '/admin/users/pending', label: 'Pending Approvals', icon: '⏳', roles: ['platform_admin'], feature: 'multiTenant' },
  { path: '/admin/users', label: 'Team Members', icon: '👥', roles: ['platform_admin', 'cra_admin'] },
  { path: '/admin/inventory', label: 'Analyst Inventory', icon: '📋', roles: ['platform_admin', 'cra_admin'] },
  { path: '/admin/organizations', label: 'Organizations', icon: '🏢', roles: ['platform_admin'], feature: 'multiTenant' },
  { path: '/admin/organizations/create', label: 'Create Organization', icon: '➕', roles: ['platform_admin'], feature: 'multiTenant' },
  { path: '/admin/licenses', label: 'Licenses', icon: '📜', roles: ['platform_admin', 'cra_admin'], feature: 'licensing' },
  { path: '/admin/reports-config', label: 'Reports Config', icon: '⚙️', roles: ['platform_admin'], feature: 'platformAdmin' },
  { path: '/admin/data-upload', label: 'Data Upload', icon: '📤', feature: 'dataUpload' },
];

/**
 * Role assumption sidebar section - only rendered in SaaS mode.
 * Separated so that useRoleAssumption is only called when the provider exists.
 */
const RoleAssumptionSection: React.FC = () => {
  const { isAssuming, effectiveRole } = useRoleAssumption();

  return (
    <>
      {isAssuming && effectiveRole && (
        <p className="admin-role assuming">
          Viewing as: {ROLE_LABELS[effectiveRole]}
        </p>
      )}
      <div className="admin-role-switcher">
        <RoleSwitcher />
      </div>
    </>
  );
};

export const AdminLayout: React.FC = () => {
  const { userProfile, logout } = useAuth();
  const featureConfig = useFeatureConfig();
  const hasRoleAssumption = useFeature('roleAssumption');
  const {
    effectiveOrganization,
    effectiveIsPlatformAdmin,
    effectiveIsCRAAdmin,
    effectiveIsBankAdmin,
  } = useRoleAssumption();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Filter nav items based on deployment features and effective role
  // (respects role assumption in SaaS; uses real role in on-prem)
  const filteredNavItems = navItems.filter(item => {
    // Check feature flag first
    if (item.feature && !featureConfig[item.feature]) return false;
    // Then check role
    if (!item.roles) return true;
    if (effectiveIsPlatformAdmin) return true;
    if (effectiveIsCRAAdmin && item.roles.includes('cra_admin')) return true;
    if (effectiveIsBankAdmin && item.roles.includes('bank_admin')) return true;
    return false;
  });

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <h2>Admin Panel</h2>
          <p className="admin-role">
            {userProfile?.role?.replace('_', ' ')}
          </p>
          {/* Role Switcher and assumption display (SaaS only) */}
          {hasRoleAssumption && <RoleAssumptionSection />}
        </div>

        <nav className="admin-nav">
          {filteredNavItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/admin'}
              className={({ isActive }) =>
                `admin-nav-item ${isActive ? 'active' : ''}`
              }
            >
              <span className="admin-nav-icon">{item.icon}</span>
              <span className="admin-nav-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-user-info">
            <p className="admin-user-email">{userProfile?.email}</p>
            <p className="admin-user-org">
              {effectiveOrganization?.name || 'No organization'}
            </p>
          </div>
          <button onClick={() => navigate('/')} className="admin-back-btn">
            ← Back to App
          </button>
          <button onClick={handleLogout} className="admin-logout-btn">
            Logout
          </button>
        </div>
      </aside>

      <main className="admin-content">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
